import { Influencer, CollabInviteTokenErrorDto } from '@foodfluence/shared';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Normalizes to E.164, the form Firebase stores verified numbers in. Firestore phone lookups and the
 * backend's verified-phone check compare against that exactly. libphonenumber drops the local
 * dialling prefix people type (AU/UK 0, HU 06, RU 8, AR 0…15) and keeps the ones that belong to the
 * number (Italy's 0). The bare-digits candidate covers a number typed with its own country code or
 * the 00 international prefix.
 */
const toE164 = (digits: string, countryCode: string): string => {
  const withCountryCode = `${countryCode}${digits}`;
  for (const candidate of [withCountryCode, `+${digits.replace(/^00/, '')}`]) {
    const parsed = parsePhoneNumberFromString(candidate);
    if (parsed?.isValid()) return parsed.number;
  }
  // Not valid per the bundled metadata (could be a newly issued range): still normalize, let Firebase decide.
  return parsePhoneNumberFromString(withCountryCode)?.number ?? withCountryCode;
};

/**
 * Formats a phone number into international (E.164) format
 * @param phoneNumber - The phone number to format
 * @param countryCode - Optional country code (with + prefix). If not provided, assumes it's included in phoneNumber
 * @returns Formatted phone number in international format (e.g., +18472753016)
 */
export const formatPhoneNumber = (phoneNumber: string, countryCode?: string): string => {
  // Remove all non-digit characters except the + sign
  const sanitizedPhone = phoneNumber.replace(/[^\d+]/g, '');
  
  // Already international: split off the country code and normalize the rest
  if (sanitizedPhone.startsWith('+')) {
    return parsePhoneNumberFromString(sanitizedPhone)?.number ?? sanitizedPhone;
  }
  
  if (countryCode) {
    // Make sure country code starts with +
    const formattedCountryCode = countryCode.startsWith('+') 
      ? countryCode 
      : `+${countryCode}`;
    
    return toE164(sanitizedPhone, formattedCountryCode);
  }
  
  // If no country code provided and phone doesn't start with +, add + as default
  return `+${sanitizedPhone}`;
};

/**
 * Combines country code and phone number into a properly formatted international phone number
 * @param countryCode - Country code (with or without + prefix)
 * @param phoneNumber - Phone number (will be sanitized)
 * @returns Formatted phone number in international format
 */
export const combinePhoneNumber = (countryCode: string, phoneNumber: string): string =>
  formatPhoneNumber(phoneNumber.replace(/\D/g, ''), countryCode);

const PHONE_AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-phone-number': "That phone number isn't valid. Check the country code and number.",
  'auth/missing-phone-number': 'Please enter your phone number.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/quota-exceeded': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/provider-already-linked': 'A phone number is already linked to your account.',
  'auth/credential-already-in-use': 'This phone number is already linked to another account.',
  'auth/operation-not-allowed': "We can't send codes to this country yet. Please contact support.",
  'auth/captcha-check-failed': 'Security check failed. Please refresh the page and try again.',
};

/**
 * User-facing message for a Firebase phone-auth error. Unknown errors keep their code so support can diagnose them.
 */
export const getPhoneAuthErrorMessage = (error: unknown): string => {
  const code = (error as { code?: unknown })?.code;
  if (typeof code !== 'string') return 'Failed to send verification code. Please try again.';
  return PHONE_AUTH_ERROR_MESSAGES[code] ?? `Failed to send verification code. Please try again. (${code})`;
};

/**
 * Checks if user should see verification overlays
 * @param user - Firebase user object  
 * @param influencerData - Influencer data from context
 * @returns boolean - true if user should see overlays, false otherwise
 */
export const shouldShowVerificationOverlay = (user: any, influencerData: Influencer | null): boolean => {
  return !!(user && !influencerData?.phoneNumber);
};

/**
 * Generates error message for collab invite token errors
 * @param error - Error object from token verification
 * @returns Error message string
 */
export const getCollabInviteErrorMessage = (error: CollabInviteTokenErrorDto): string => {
  if (error.tokenAlreadyUsed) {
    return error.hasPhoneNumber 
      ? 'This invite link has already been used. Please sign in.'
      : 'This invite link has expired. Please sign up to continue.';
  }
  return error.message || error.error || 'Invalid or expired invite link';
};

/**
 * Determines the redirect path for a collab based on its status
 * @param collabId - The collab ID
 * @param status - The collab status
 * @returns The redirect path
 */
export const getCollabRedirectPath = (collabId: string, status?: string): string => {
  if (status && status !== 'pendingInfluencerApproval') {
    return '/collabs';
  }
  return `/collabs/review-collab/${collabId}`;
};

/**
 * Checks if an email is from foodfluence.ai or foodfluence.io domain
 * @param email - The email address to check
 * @returns boolean - true if email is from foodfluence.ai or foodfluence.io
 */
export const isFoodfluenceEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const emailLower = email.toLowerCase();
  return emailLower.endsWith('@foodfluence.ai') || emailLower.endsWith('@foodfluence.io') || emailLower.endsWith('@joinfoodfluence.com');
};